import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Palette, Image, Upload } from 'lucide-react';
import { sportsApi } from '../SportsAuthContext';
import { THEME_LIST } from '../themes';

// Theme & Appearance — how the public site looks: color theme, site mode,
// facility logo. Moved here from Settings (owner decision 2026-08-18); it
// styles the public site, so it lives with the Site Builder. Saved through
// the builder's Save (top-level settings fields — the PUT is a partial $set).
// The theme grid derives from ../themes (single source) — adding a preset
// there makes it appear here automatically.

const SITE_MODES = [
  { id: 'booking_only', label: 'Booking Only', desc: 'Show booking widget only' },
  { id: 'full', label: 'Full Page', desc: 'About + Gallery + Events + Booking' },
];

export default function ThemeAppearance({ theme, siteMode, logoUrl, onThemeChange, onSiteModeChange, onLogoKey }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', 'logo');
      const res = await sportsApi.post('/sports/settings/upload-file', formData);
      setPreview(URL.createObjectURL(file));
      onLogoKey(res.data.key);
      toast.success('Logo uploaded');
    } catch {
      toast.error('Upload failed. Try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const shownLogo = preview || logoUrl;

  return (
    <div className="rounded-xl p-5 mb-5" data-testid="site-builder-theme" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      <div className="flex items-center gap-2 mb-1">
        <Palette className="h-4 w-4" style={{ color: '#E63946' }} />
        <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Theme &amp; Appearance</h2>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>
        How your public site looks. Changes apply when you save.
      </p>

      <span className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Color theme</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {THEME_LIST.map(t => (
          <button key={t.id} data-testid={`sports-settings-theme-${t.id.replace(' ', '-').toLowerCase()}`}
            onClick={() => onThemeChange(t.id)}
            className="flex items-center gap-3 p-3 rounded-xl transition-all text-left"
            style={{
              background: theme === t.id ? t.primary + '14' : '#0A0A0A',
              border: `1px solid ${theme === t.id ? t.primary : '#2A2A2A'}`,
              cursor: 'pointer',
            }}>
            <div className="h-10 w-10 rounded-lg shrink-0" style={{ background: t.bg, border: `3px solid ${t.primary}` }}>
              <div className="h-full w-full rounded-md" style={{ background: `linear-gradient(135deg, ${t.primary}80, transparent)` }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: theme === t.id ? t.primary : '#fff' }}>{t.label}</p>
              <p className="text-xs" style={{ color: '#555' }}>{t.desc}</p>
            </div>
          </button>
        ))}
      </div>

      <span className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Site mode</span>
      <div className="flex gap-3 mb-5">
        {SITE_MODES.map(m => (
          <button key={m.id} onClick={() => onSiteModeChange(m.id)}
            data-testid={`site-builder-mode-${m.id}`}
            className="flex-1 p-3 rounded-xl text-left transition-all"
            style={{
              background: siteMode === m.id ? '#E6394614' : '#0A0A0A',
              border: `1px solid ${siteMode === m.id ? '#E63946' : '#2A2A2A'}`,
              cursor: 'pointer',
            }}>
            <p className="text-sm font-bold" style={{ color: siteMode === m.id ? '#E63946' : '#fff' }}>{m.label}</p>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>{m.desc}</p>
          </button>
        ))}
      </div>

      <span className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>Facility logo</span>
      <div className="flex items-center gap-4">
        {shownLogo ? (
          <img src={shownLogo} alt="Facility logo" className="h-16 w-16 rounded-lg object-cover" style={{ border: '1px solid #2A2A2A' }} />
        ) : (
          <div className="h-16 w-16 rounded-lg flex items-center justify-center" style={{ background: '#1A1A1A', border: '1px dashed #2A2A2A' }}>
            <Image className="h-6 w-6" style={{ color: '#555' }} />
          </div>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="site-builder-logo-upload"
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          style={{ background: '#2A2A2A', color: '#A3A3A3', border: 'none', cursor: 'pointer' }}>
          {uploading ? 'Uploading…' : <><Upload className="h-3.5 w-3.5" /> Upload</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
      </div>
    </div>
  );
}
